import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
import { api } from '@/lib/api';
import { documentDisplayTitle } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import {
  autoPushOutcomeOf,
  rowPushReport,
  type XeroPushReport,
  type XeroRowPushReport,
} from '@/lib/xero';
import type { Invoice } from '@/lib/types';

/**
 * AN INVOICE NUMBER ON A REVENUE SURFACE — a real link that OPENS THE EDIT FORM.
 *
 * Owner's instruction: clicking an invoice number on `/revenue-tracker` or on the
 * Management Dashboard's *Revenue by company* tab should open the invoice in a
 * modal rather than navigating away. Both surfaces share this file so they cannot
 * word, gate or reload the behaviour differently — the drift this repo has
 * already shipped once, on a refusal described in two tones across the quote and
 * the invoice page.
 *
 * ── 🔴 IT IS STILL A LINK, AND THAT IS THE HALF MOST EASILY LOST ───────────
 *
 * The `href` is a real `/invoices/{id}`, so ctrl/cmd-click, middle-click, "open
 * in new tab" and "copy link address" all still work and still land on the detail
 * page. {@link InvoiceNumberLink} intercepts ONLY the plain primary click, and it
 * returns early — doing nothing, so the browser and the router handle it — on a
 * modified click, a non-primary button, or an event something else has already
 * claimed. **A `<button>` styled as a link would have taken all of that away
 * silently**, which is the one regression nobody reports: a person who cannot
 * open a row in a new tab concludes the app is like that.
 *
 * ── ⚠️ ONE MODAL PER SURFACE, NOT ONE PER ROW ──────────────────────────────
 *
 * The state and the form live in {@link InvoiceQuickEdit}, which a surface mounts
 * ONCE. A modal inside the row renderer would be one mounted form per invoice
 * number on screen — hundreds of them on the tracker's cross-tab, each with its
 * own pickers to populate — so the link component holds no state of its own and
 * reaches the host through context.
 *
 * ⚠️ **A link with NO host above it navigates, and does not throw.** The default
 * context offers no opener, which is exactly what a reader who may not edit gets
 * too, so there is one code path for "this click is an ordinary navigation".
 *
 * ── 🔴 THE ROWS CARRY AN ID, NOT AN INVOICE ────────────────────────────────
 *
 * `InvoiceFormModal` takes a whole {@link Invoice}, and a revenue row holds only
 * `invoiceId` and `invoiceNumber` — so a click is a FETCH before it is a modal.
 * That is why the link has a busy state and the host has a failure state: a click
 * that silently does nothing is indistinguishable from a dead control.
 */

/* -------------------------------------------------------------------------
 * The context
 * --------------------------------------------------------------------- */

interface InvoiceQuickEditContextValue {
  /**
   * `null` means **navigate instead** — no host is mounted, or this reader may
   * not update invoices.
   *
   * ⚠️ **Two different facts collapsed into one deliberately.** Both mean the
   * link must behave as an ordinary link, and keeping them apart would tempt a
   * call site into rendering a disabled control or a refusal beside a number
   * that is perfectly readable on its own page.
   */
  open: ((invoiceId: number, invoiceNumber: string) => void) | null;
  /** The invoice currently being fetched, so the row that asked can say so. */
  pendingId: number | null;
}

const InvoiceQuickEditContext = createContext<InvoiceQuickEditContextValue>({
  open: null,
  pendingId: null,
});

/* -------------------------------------------------------------------------
 * The link
 * --------------------------------------------------------------------- */

export interface InvoiceNumberLinkProps {
  invoiceId: number;
  /**
   * What the row has to show. Rendered verbatim.
   *
   * ⚠️ **It is NOT derived from `invoiceId` and the two are INDEPENDENTLY
   * nullable on both surfaces** — an invoice raised inside Xero can carry a
   * number with no local id, and a local draft an id with no number. Deciding
   * which of those a row is in stays with the caller, which is the only side
   * that can see the shape it was given; this component is mounted only where
   * both exist.
   */
  invoiceNumber: string;
  className?: string;
  /** The truncation channel, exactly as on the plain `Link` this replaces. */
  title?: string;
}

/**
 * One invoice number: a link to `/invoices/{id}` that opens the edit form in
 * place on a plain click.
 */
export function InvoiceNumberLink({
  invoiceId,
  invoiceNumber,
  className,
  title,
}: InvoiceNumberLinkProps): ReactElement {
  const { open, pendingId } = useContext(InvoiceQuickEditContext);
  const busy = pendingId === invoiceId;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    /*
     * ⛔ **EVERY ONE OF THESE IS A RETURN, NOT A BRANCH.** Falling through
     * without calling `preventDefault` leaves the event exactly as the browser
     * and the router expect it, so a cmd-click opens a tab and a middle-click
     * opens a background one — the behaviours a link is chosen FOR.
     *
     * ⚠️ `react-router`'s own handler runs after this one and makes the same
     * checks, so on a modified click both stand down. The checks are written out
     * here anyway rather than leant on: this component's contract is that the
     * modifier is honoured, and a contract held only by another library's
     * internals is one nothing in this repo can see change.
     */
    if (open === null) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    open(invoiceId, invoiceNumber);
  };

  return (
    <Link
      to={`/invoices/${invoiceId}`}
      className={className}
      {...(title !== undefined ? { title } : {})}
      /*
       * ⚠️ **`aria-busy` AND NOTHING ELSE WHILE THE FETCH RUNS.** The link is
       * deliberately NOT disabled: a second click re-issues the read, which is
       * the right answer on a request that has stalled, and an anchor that
       * disables itself also stops being reachable by keyboard and stops being
       * a link for the modifier clicks above.
       */
      {...(busy ? { 'aria-busy': true } : {})}
      onClick={handleClick}
    >
      {invoiceNumber}
      {/*
       * ⚠️ **A VISIBLE MARK, because `aria-busy` alone leaves a sighted reader
       * with a click that appears to have done nothing** — the fetch is a whole
       * round trip and the modal cannot open until it lands. An ellipsis rather
       * than a spinner: it needs no animation to be legible, it survives
       * `motion-reduce`, and it costs the cell no height in either of the two
       * dense layouts this renders in.
       *
       * `aria-hidden`, because `aria-busy` is already saying this to assistive
       * tech and a bare ellipsis read aloud says nothing.
       */}
      {busy && (
        <span aria-hidden="true" className="ml-1 text-fg-subtle">
          …
        </span>
      )}
    </Link>
  );
}

/* -------------------------------------------------------------------------
 * The host
 * --------------------------------------------------------------------- */

/**
 * What a save's Xero half is worth SAYING on a revenue surface.
 *
 * 🔴 **A HEADER PATCH REACHES XERO.** `invoices.controller.update` calls
 * `autoPushAndRefresh` → `autoPushDocument`, so an edit made from a dashboard can
 * create or overwrite a document in the accountant's Xero. The server swallows a
 * failed push so the save still answers `200`, and `xeroPush` on the envelope is
 * the ONLY artefact anywhere that carries it — a caller that drops it has changed
 * a customer's document, told the person it worked, and left no trace of Xero not
 * having got it.
 *
 * ⛔ **`skipped` IS THE ORDINARY OUTCOME AND IS SUPPRESSED, NEVER WORDED AS A
 * FAILURE.** It is returned before Xero is called at all for a document with no
 * line items and for a company nobody has linked to a Xero contact. Rendering it
 * as a problem would put a warning strip over the commonest save on the system
 * and teach people to disregard the one that matters. `updated` is suppressed for
 * the mirror reason: nothing went wrong and the figures on screen say so.
 *
 * ⚠️ **EVERY HEADING LEADS WITH THE INVOICE HAVING BEEN SAVED**, which is
 * load-bearing rather than courteous: Xero does not enforce invoice-number
 * uniqueness, so a person who reads a refused push as *"my save was lost"* and
 * saves again is the path to two live demands for money against one customer.
 * `rowPushReport`'s own headings are written for a list row where nothing was
 * just saved, so only its LINES — the SERVER's own sentences — and its pointer
 * are kept. Nothing here invents a sentence about the cause.
 */
function savedPushReport(
  invoice: Invoice,
  report: XeroPushReport | undefined,
): XeroRowPushReport | null {
  const outcome = autoPushOutcomeOf(report);
  /*
   * `null` covers an absent report — a server predating `xeroPush`, or a
   * rollback — and a status this build has never heard of. Both are decided in
   * `autoPushOutcomeOf`, and neither is something to guess a tone for.
   */
  if (outcome === null) return null;
  if (outcome.kind === 'updated' || outcome.kind === 'skipped') return null;

  const name = documentDisplayTitle(invoice.subject, invoice.xeroInvoiceNumber);
  const base = rowPushReport('invoices', name, outcome);

  /*
   * ⚠️ **`settled` IS NOT A FAILURE AND IS TONED BY `rowPushReport` AS `info`.**
   * Once anybody approves or sends the invoice from inside Xero, every later push
   * is refused — the design working, and the common answer on a paid invoice,
   * which is the only kind a revenue surface shows. It still has to be SAID: the
   * reader has just changed a figure here that Xero's copy will not follow.
   */
  if (outcome.kind === 'settled') {
    return { ...base, heading: `“${name}” was saved here. Xero’s copy was not changed.` };
  }

  return { ...base, heading: `“${name}” was saved, and Xero would not take the change.` };
}

export interface InvoiceQuickEditProps {
  /**
   * Re-read the surface's own data. **Not optional.**
   *
   * ⚠️ **A SAVED INVOICE CAN CHANGE THE VERY FIGURES THE READER IS LOOKING AT** —
   * both surfaces are built from paid invoice lines, so a currency, a status or a
   * line edit moves the amounts, the totals and which rows appear at all. Leaving
   * a dashboard showing figures the save has just invalidated is the failure this
   * prop exists to prevent, and it is silent: nothing errors, the numbers are
   * merely the ones from before.
   */
  onSaved: () => void;
  children: ReactNode;
}

/**
 * The one mounted edit form behind every {@link InvoiceNumberLink} beneath it.
 *
 * ⚠️ **MOUNT IT ONCE PER SURFACE.** Two hosts on one page would each hold their
 * own open invoice and their own notices, and a link would reach whichever is
 * nearer in the tree — which is not a thing a reader could be told.
 */
export function InvoiceQuickEdit({ onSaved, children }: InvoiceQuickEditProps): ReactElement {
  const { ability } = useAuth();

  /*
   * ⛔ **THE FORM DOES NOT GATE ITS OWN SAVE, SO THE GATE HAS TO BE HERE.**
   * Measured in `InvoiceFormModal`: `saveDisabled` on an edit reads
   * `(!isDirty && lineEdits.dirty === 0) || lineEdits.blocked > 0` and consults
   * no ability at all — every existing caller gates the AFFORDANCE instead
   * (`InvoicesPage` renders its Edit button behind `ability.canUpdate('invoices')`).
   * So a reader who cannot write invoices is given the ordinary navigation: the
   * detail page, which shows them everything and offers nothing that 403s.
   *
   * ⚠️ **`ability`, never `user.role` and never `/permissions/me`** — the rule
   * `src/lib/permissions.ts` states at length. The gate is the optimisation and
   * the server's 403 is the guarantee; an access token lasts days, so the role
   * can change under this page.
   */
  const canOpen = ability.canUpdate('invoices');

  const [pendingId, setPendingId] = useState<number | null>(null);
  const [failure, setFailure] = useState<{ invoiceNumber: string; message: string } | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [push, setPush] = useState<XeroRowPushReport | null>(null);

  /*
   * ⚠️ **ONE CONTROLLER, ABORTED BY THE NEXT CLICK AND BY UNMOUNT.** Every `.catch`
   * and `.finally` below guards on `signal.aborted` — the house rule — so a read
   * this component has stopped caring about can neither raise a failure notice
   * nor clear a busy state that now belongs to a later click.
   */
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const open = useCallback((invoiceId: number, invoiceNumber: string) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setFailure(null);
    setPush(null);
    setPendingId(invoiceId);

    void api
      .getInvoice(invoiceId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setInvoice(envelope.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setFailure({
          invoiceNumber,
          message: caught instanceof Error ? caught.message : 'The request did not complete.',
        });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setPendingId(null);
      });
  }, []);

  const context = useMemo<InvoiceQuickEditContextValue>(
    () => ({ open: canOpen ? open : null, pendingId }),
    [canOpen, open, pendingId],
  );

  return (
    <InvoiceQuickEditContext.Provider value={context}>
      {/*
       * ⚠️ **THE NOTICES SIT ABOVE THE CONTENT, not beside the row that was
       * clicked.** A saved invoice reloads the surface, and a row can legitimately
       * leave it — the figures are built from paid invoice lines — so a notice
       * anchored to the row would take the Xero report with it exactly when the
       * report is the only record that Xero has not got the change.
       */}
      {failure !== null && (
        <Notice
          tone="danger"
          role="alert"
          /*
           * ⚠️ **IT NAMES THE INVOICE.** Both surfaces can show dozens of
           * numbers at once, and "an invoice could not be opened" sends the
           * reader hunting for which.
           */
          title={
            <span className="[overflow-wrap:anywhere]">
              Invoice {failure.invoiceNumber} could not be opened.
            </span>
          }
          action={
            <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setFailure(null)}>
              Dismiss
            </Button>
          }
        >
          {/* The server's own sentence, never replaced by one of ours. */}
          <span className="[overflow-wrap:anywhere]">{failure.message}</span>
        </Notice>
      )}

      {push !== null && (
        <div className="overflow-hidden rounded-md border border-border">
          {/*
           * Rendered by the component the list rows and `InvoiceFormModal`
           * already use, so one server code cannot be worded three ways. See
           * {@link savedPushReport} for the only part that is ours.
           */}
          <XeroPushReportBanner report={push} onDismiss={() => setPush(null)} />
        </div>
      )}

      {children}

      {/*
       * Mounted only while an invoice is open, so the form's state is fresh on
       * every open rather than relying on its remount key to notice the change.
       */}
      {invoice !== null && (
        <InvoiceFormModal
          open
          invoice={invoice}
          /*
           * ⚠️ **`'unknown'`, NEVER `false`, AND THIS IS THE ONE PROP TO GET
           * RIGHT.** Neither revenue surface knows how many line items an
           * invoice has — `GET /revenue-tracker` carries none and neither does
           * the analytics read — and `'unknown'` LOCKS the four header totals.
           * Offering those inputs on an invoice that has lines is the one state
           * the money design forbids: a header PATCH writes exactly what it is
           * given and the next line write silently replaces it. Refusing them
           * merely sends somebody to the detail page, which knows the count.
           * `InvoicesPage` passes the same value for the same reason.
           */
          hasLineItems="unknown"
          onClose={() => setInvoice(null)}
          onSaved={() => {
            /*
             * ⚠️ **`onSaved` IS A CLOSE**, and the form calls `onClose` straight
             * after this — so the close below is belt-and-braces rather than the
             * mechanism. What matters here is the reload: see
             * {@link InvoiceQuickEditProps.onSaved}.
             */
            setInvoice(null);
            onSaved();
          }}
          /*
           * ⚠️ **A SECOND, NARROWER SIGNAL, and deliberately not `onSaved`.** The
           * line grid saves on its own button while this form stays open; the
           * figures behind are stale from that instant, and the person is still
           * editing. Reload, close nothing.
           */
          onLinesSaved={onSaved}
          /*
           * ⚠️ **The report, never a push.** The PATCH has already been to Xero
           * by the time this fires — the server pushes inside the save — so
           * passing it buys the reader the truth and passing nothing buys
           * silence, never safety.
           */
          onXeroPush={(report) => setPush(savedPushReport(invoice, report))}
        />
      )}
    </InvoiceQuickEditContext.Provider>
  );
}
